// ============================================
// Sprite - Simple sprite object
// ============================================

import { type TextureHandle, packSortKey, NULL_HANDLE } from '../types';

/**
 * Sprite - A 2D textured quad with transform
 */
export class Sprite {
  // Transform
  x = 0;
  y = 0;
  width = 0;
  height = 0;
  rotation = 0;
  scaleX = 1;
  scaleY = 1;
  anchorX = 0;
  anchorY = 0;
  
  // Texture
  texture: TextureHandle = NULL_HANDLE;
  u0 = 0;
  v0 = 0;
  u1 = 1;
  v1 = 1;
  
  // Color/Tint (0-1 range)
  r = 1;
  g = 1;
  b = 1;
  alpha = 1;
  
  // Sorting
  z = 0;
  layer = 0;
  
  // Material id (for batching)
  materialId = 0;
  
  // Visibility
  visible = true;
  
  constructor(texture?: TextureHandle, width?: number, height?: number) {
    if (texture !== undefined) this.texture = texture;
    if (width !== undefined) this.width = width;
    if (height !== undefined) this.height = height;
  }
  
  /**
   * Set position
   */
  setPosition(x: number, y: number): this {
    this.x = x;
    this.y = y;
    return this;
  }
  
  /**
   * Set size
   */
  setSize(width: number, height: number): this {
    this.width = width;
    this.height = height;
    return this;
  }
  
  /**
   * Set scale
   */
  setScale(scaleX: number, scaleY = scaleX): this {
    this.scaleX = scaleX;
    this.scaleY = scaleY;
    return this;
  }
  
  /**
   * Set anchor point (0-1)
   */
  setAnchor(x: number, y = x): this {
    this.anchorX = x;
    this.anchorY = y;
    return this;
  }
  
  /**
   * Set UV coordinates
   */
  setUV(u0: number, v0: number, u1: number, v1: number): this {
    this.u0 = u0;
    this.v0 = v0;
    this.u1 = u1;
    this.v1 = v1;
    return this;
  }
  
  /**
   * Set tint color (0-1 range)
   */
  setTint(r: number, g: number, b: number): this {
    this.r = r;
    this.g = g;
    this.b = b;
    return this;
  }
  
  /**
   * Set tint from hex color
   */
  setTintHex(hex: number): this {
    this.r = ((hex >> 16) & 0xFF) / 255;
    this.g = ((hex >> 8) & 0xFF) / 255;
    this.b = (hex & 0xFF) / 255;
    return this;
  }
  
  /**
   * Get packed sort key
   */
  getSortKey(): number {
    return packSortKey(this.layer, this.z, this.materialId);
  }
  
  /**
   * Get world-space bounds
   */
  getBounds(): { minX: number; minY: number; maxX: number; maxY: number } {
    const w = this.width * this.scaleX;
    const h = this.height * this.scaleY;
    const ax = w * this.anchorX;
    const ay = h * this.anchorY;
    
    if (this.rotation === 0) {
      return {
        minX: this.x - ax,
        minY: this.y - ay,
        maxX: this.x - ax + w,
        maxY: this.y - ay + h,
      };
    }
    
    // Rotated bounds (AABB of rotated quad)
    const cos = Math.cos(this.rotation);
    const sin = Math.sin(this.rotation);
    
    // Transform corners
    const corners = [
      { x: -ax, y: -ay },
      { x: w - ax, y: -ay },
      { x: w - ax, y: h - ay },
      { x: -ax, y: h - ay },
    ];
    
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    
    for (const c of corners) {
      const rx = this.x + c.x * cos - c.y * sin;
      const ry = this.y + c.x * sin + c.y * cos;
      minX = Math.min(minX, rx);
      minY = Math.min(minY, ry);
      maxX = Math.max(maxX, rx);
      maxY = Math.max(maxY, ry);
    }
    
    return { minX, minY, maxX, maxY };
  }
  
  /**
   * Clone this sprite
   */
  clone(): Sprite {
    const s = new Sprite();
    s.x = this.x;
    s.y = this.y;
    s.width = this.width;
    s.height = this.height;
    s.rotation = this.rotation;
    s.scaleX = this.scaleX;
    s.scaleY = this.scaleY;
    s.anchorX = this.anchorX;
    s.anchorY = this.anchorY;
    s.texture = this.texture;
    s.u0 = this.u0;
    s.v0 = this.v0;
    s.u1 = this.u1;
    s.v1 = this.v1;
    s.r = this.r;
    s.g = this.g;
    s.b = this.b;
    s.alpha = this.alpha;
    s.z = this.z;
    s.layer = this.layer;
    s.materialId = this.materialId;
    s.visible = this.visible;
    return s;
  }
}

/**
 * Quad - A custom 4-vertex quad with arbitrary vertex positions
 */
export class Quad {
  // Vertices (x, y for each corner)
  v0x = 0; v0y = 0;
  v1x = 0; v1y = 0;
  v2x = 0; v2y = 0;
  v3x = 0; v3y = 0;
  
  // UVs for each vertex
  u0 = 0; uv0 = 0;
  u1 = 1; uv1 = 0;
  u2 = 1; uv2 = 1;
  u3 = 0; uv3 = 1;
  
  // Color for each vertex (packed RGBA)
  c0 = 0xFFFFFFFF;
  c1 = 0xFFFFFFFF;
  c2 = 0xFFFFFFFF;
  c3 = 0xFFFFFFFF;
  
  // Texture
  texture: TextureHandle = NULL_HANDLE;
  
  // Sorting
  z = 0;
  layer = 0;
  
  /**
   * Set from rectangle
   */
  setRect(x: number, y: number, width: number, height: number): this {
    this.v0x = x;        this.v0y = y;
    this.v1x = x + width; this.v1y = y;
    this.v2x = x + width; this.v2y = y + height;
    this.v3x = x;        this.v3y = y + height;
    return this;
  }
  
  /**
   * Set all vertex colors
   */
  setColor(color: number): this {
    this.c0 = this.c1 = this.c2 = this.c3 = color;
    return this;
  }
  
  /**
   * Set vertex colors for gradient
   */
  setColors(c0: number, c1: number, c2: number, c3: number): this {
    this.c0 = c0;
    this.c1 = c1;
    this.c2 = c2;
    this.c3 = c3;
    return this;
  }
}
